import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { loadConnectedAiApiKeyIntegrationsFromBrowser } from 'utils/ai-integrations-browser-storage';
import { fetchGeminiModelsForGenerateContent } from 'utils/gemini-list-models';
import { GEMINI_MODEL_FALLBACK } from './AIGeneratorServices';

const GEMINI_INTEGRATION_ID = 'google_gemini_integration';

export type SmartTemplateBriefDialogProps = {
  open: boolean;
  onClose: () => void;
  /** User brief + model id (same ids as Integrations / ListModels). */
  onSubmitBrief: (brief: string, modelId: string) => void | Promise<void>;
  loading?: boolean;
};

const SmartTemplateBriefDialog: React.FC<SmartTemplateBriefDialogProps> = ({
  open,
  onClose,
  onSubmitBrief,
  loading = false,
}) => {
  const [brief, setBrief] = useState('');
  const [selectedModel, setSelectedModel] = useState<string>(GEMINI_MODEL_FALLBACK);
  const [modelOptions, setModelOptions] = useState<string[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setBrief('');
      setModelsError(null);
      return;
    }

    const rows = loadConnectedAiApiKeyIntegrationsFromBrowser();
    const gemini = rows.find((r) => r.id === GEMINI_INTEGRATION_ID && r.status === 'connected');
    const apiKey = gemini?.config?.apiKey?.trim();
    const connectionDefault = gemini?.config?.defaultModel?.trim() || '';

    if (!apiKey) {
      setModelOptions([GEMINI_MODEL_FALLBACK]);
      setSelectedModel(GEMINI_MODEL_FALLBACK);
      return;
    }

    setModelsLoading(true);
    setModelsError(null);

    void fetchGeminiModelsForGenerateContent(apiKey).then((res) => {
      setModelsLoading(false);
      if (res.ok && res.modelIds.length > 0) {
        setModelOptions(res.modelIds);
        const preferred =
          connectionDefault && res.modelIds.includes(connectionDefault)
            ? connectionDefault
            : res.modelIds.includes(GEMINI_MODEL_FALLBACK)
              ? GEMINI_MODEL_FALLBACK
              : res.modelIds[0];
        setSelectedModel(preferred);
      } else {
        const fallback = connectionDefault || GEMINI_MODEL_FALLBACK;
        setModelOptions([fallback]);
        setSelectedModel(fallback);
        if (!res.ok) {
          setModelsError(res.error);
        }
      }
    });
  }, [open]);

  const handleSubmit = () => {
    const t = brief.trim();
    if (!t || loading || modelsLoading) return;
    void onSubmitBrief(t, selectedModel);
  };

  const disableSubmit = !brief.trim() || loading || modelsLoading || !selectedModel;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>ניסוח חכם (AI)</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          תאר בקצרה את מטרת התבנית, קהל יעד וסגנון — המודל יחזיר JSON מובנה (שמות, גוף, כפתורים)
          לפי מדיניות Meta.
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={loading || modelsLoading}>
          <InputLabel id="smart-template-model-label">מודל</InputLabel>
          <Select
            labelId="smart-template-model-label"
            label="מודל"
            value={modelOptions.includes(selectedModel) ? selectedModel : modelOptions[0] ?? GEMINI_MODEL_FALLBACK}
            onChange={(e) => setSelectedModel(String(e.target.value))}
          >
            {(modelOptions.length ? modelOptions : [GEMINI_MODEL_FALLBACK]).map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            ברירת המחדל נלקחת מהחיבור Gemini באינטגרציות; ניתן לשנות לכל מודל זמין בחשבון.
          </Typography>
          {modelsError && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
              לא נטענה רשימת מודלים מ-Google ({modelsError}). משתמשים במודל הנבחר ידנית.
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          multiline
          minRows={4}
          label="תיאור התבנית"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          disabled={loading}
          placeholder="לדוגמה: תבנית עדכון סטטוס הזמנה עם שם לקוח ומספר הזמנה..."
        />
        {(loading || modelsLoading) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">{modelsLoading ? 'טוען מודלים…' : 'יוצר תבנית…'}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          ביטול
        </Button>
        <Button variant="contained" color="secondary" onClick={handleSubmit} disabled={disableSubmit}>
          יצירה
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SmartTemplateBriefDialog;
